/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import SelectField from "@/components/common/select";
import { Renderify } from "renderify-js";
import {
  useGetCoreServices,
  useGetServiceEndpoints,
} from "@/queries/interoperability/interoperability.api";
import type { VariableType } from "../../calculated-field/CalculatedFieldOperation/types";

interface InteroperabilityConfigProps {
  institution: string;
  action: string;
  onInstitutionChange: (value: string) => void;
  onActionChange: (value: string) => void;
  onParamChange?: (value: any) => void;
  onReturnChange?: (value: any) => void;
  fieldName?: string;
  fieldKeyValue?: string;
}

// interface ParamDefinition {
//   type?: "string" | "number" | "integer" | "array" | "date" | "datetime";
//   description?: string;
//   display_name?: string;
//   icon?: string;
//   example?: string;
//   required_by?: string;
// }

// type FieldTuple = [string, ParamDefinition];

// export type FieldConfigArray = FieldTuple[];

export type InteroperabilityConfig = {
  name: string;
  type: string;
  label: string;
  placeholder: string;
  source?: {
    service: string;
    action: string;
    path: string;
    type: string;
    map: {
      value: string;
      label: string;
    };
  };
};

export type InteroperabilityConfigReturn = {
  field_name: string;
  field_key_value: string;
  name: string;
  key_name: string;
  type: VariableType;
};

const InteroperabilityConfig: React.FC<InteroperabilityConfigProps> = ({
  institution,
  action,
  onInstitutionChange,
  onActionChange,
  onParamChange,
  onReturnChange,
  fieldName,
  fieldKeyValue,
}) => {
  const { data: coreServices, isLoading: isLoadingCoreServices } =
    useGetCoreServices();
  const { data: endpoints, isLoading: isLoadingEndpoints } =
    useGetServiceEndpoints(institution);

  const handleInstitutionChange = (value: string) => {
    onInstitutionChange(value);
    onActionChange("");
  };

  React.useEffect(() => {
    if (action && endpoints) {
      const selectedEndpoint = endpoints.find(
        (e: { id: string }) => e.id === action,
      );
      const params = selectedEndpoint?.service?.request?.params;

      console.log(params);

      if (
        params &&
        typeof params === "object" &&
        Object.keys(params).length > 0
      ) {
        const keys = Object.keys(params);

        onParamChange?.(
          Object.values(params).map((param: any, index: number) => {
            let placeholder = String(param.example || "");

            if (/^\d{4}-\d{2}-\d{2}$/.test(placeholder)) {
              const [year, month, day] = placeholder.split("-");
              placeholder = `${day}/${month}/${year}`;
            }

            const fieldConfig: any = {
              name: keys[index],
              type: param.type || param.field_type || "string",
              label: param.display_name,
              placeholder,
            };

            if (param.source) {
              fieldConfig.source = param.source;
            }

            return fieldConfig;
          }),
        );
      } else {
        onParamChange?.([]);
      }
    }
  }, [action, endpoints, fieldName, fieldKeyValue]);

  React.useEffect(() => {
    if (action && endpoints) {
      const selectedEndpoint = endpoints.find(
        (e: { id: string }) => e.id === action,
      );
      const params = selectedEndpoint?.service?.responses?.["200"]?.data;

      if (
        params &&
        typeof params === "object" &&
        Object.keys(params).length > 0
      ) {
        onReturnChange?.(
          Object.entries(params).map(([key, param]: [string, any]) => ({
            field_name: fieldName,
            field_key_value: fieldKeyValue,
            name: param.display_name,
            key_name: key,
            type: String(param.type || "string"),
          })),
        );
      } else {
        onReturnChange?.([]);
      }
    }
  }, [action, endpoints, fieldName, fieldKeyValue]);

  return (
    <div className="space-y-4">
      <SelectField
        name="institution"
        label="Seleccionar institución"
        placeholder="Selecciona una institución"
        value={institution}
        onChange={handleInstitutionChange}
        options={
          coreServices?.data
            ? Object.keys(coreServices.data)
                .filter((key) => {
                  const service = coreServices.data[key];
                  return (
                    typeof service === "object" &&
                    service !== null &&
                    "is_active" in service &&
                    service.is_active === true
                  );
                })
                .map((key) => ({
                  label: key.toUpperCase(),
                  value: key,
                }))
            : []
        }
        required
        isDisabled={isLoadingCoreServices}
      />

      <SelectField
        name="action"
        label="Acción a realizar"
        placeholder="Selecciona una acción"
        value={action}
        onChange={onActionChange}
        options={
          endpoints
            ? endpoints
                .filter((endpoint: any) => {
                  const isNodeSupported = endpoint?.service?.is_node_supported;
                  return isNodeSupported === true;
                })
                .map((endpoint: { id: string; name: string }) => ({
                  label: endpoint.name,
                  value: endpoint.id,
                }))
            : []
        }
        required
        isDisabled={!institution || isLoadingEndpoints}
      />

      {/* Campos para verificar */}
      <Renderify condition={!!action && !!endpoints}>
        <div className="space-y-3">
          <div className="text-sm text-body-quiet">
            Esta acción habilitará los siguientes campos para verificar...
          </div>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const selectedEndpoint = endpoints?.find(
                (e: { id: string; service: unknown }) => e.id === action,
              );
              const params = selectedEndpoint?.service?.request?.params;

              if (!params) return null;

              return Object.entries(params).map(([paramName, paramDef]) => {
                const def = paramDef as {
                  type?: string;
                  display_name?: string;
                };
                // Mapeo de tipos a iconos y etiquetas
                const typeMapping: Record<
                  string,
                  { icon: string; label: string }
                > = {
                  string: { icon: "text", label: "str" },
                  text: { icon: "text", label: "str" },
                  number: { icon: "hashtag", label: "num" },
                  integer: { icon: "hashtag", label: "num" },
                  array: { icon: "list", label: "array" },
                  date: { icon: "calendar", label: "date" },
                  datetime: { icon: "calendar", label: "date" },
                  double: { icon: "hashtag", label: "num" },
                  boolean: { icon: "toggle-on", label: "bool" },
                };

                const paramType = (def?.type || "string").toLowerCase();
                const typeInfo = typeMapping[paramType] || typeMapping.string;

                return (
                  <Badge
                    key={paramName}
                    variant="neutral"
                    size="sm"
                    shape="square"
                    className="flex items-center gap-2"
                  >
                    <Icon name={typeInfo.icon} type="far" className="text-xs" />
                    <span>{def?.display_name || paramName}</span>
                    <span className="text-body-quiet">({typeInfo.label})</span>
                  </Badge>
                );
              });
            })()}
          </div>

          <div className="text-sm text-body-quiet mt-4">
            ...y retornará los siguientes valores para su uso.
          </div>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const selectedEndpoint = endpoints?.find(
                (e: { id: string; service: unknown }) => e.id === action,
              );
              const responseData =
                selectedEndpoint?.service?.responses?.["200"]?.data;

              if (!responseData) return null;

              return Object.entries(responseData).map(
                ([paramName, paramDef]) => {
                  const def = paramDef as {
                    type?: string;
                    display_name?: string;
                  };
                  // Mapeo de tipos a iconos y etiquetas
                  const typeMapping: Record<
                    string,
                    { icon: string; label: string }
                  > = {
                    string: { icon: "text", label: "str" },
                    text: { icon: "text", label: "str" },
                    number: { icon: "hashtag", label: "num" },
                    integer: { icon: "hashtag", label: "num" },
                    double: { icon: "hashtag", label: "num" },
                    array: { icon: "list", label: "array" },
                    date: { icon: "calendar", label: "date" },
                    datetime: { icon: "calendar", label: "date" },
                    boolean: { icon: "toggle-on", label: "bool" },
                  };

                  const paramType = (def?.type || "string").toLowerCase();
                  const typeInfo = typeMapping[paramType] || typeMapping.string;

                  return (
                    <Badge
                      key={paramName}
                      variant="neutral"
                      size="sm"
                      shape="square"
                      className="flex items-center gap-2"
                    >
                      <Icon
                        name={typeInfo.icon}
                        type="far"
                        className="text-xs"
                      />
                      <span>{def?.display_name || paramName}</span>
                      <span className="text-body-quiet">
                        ({typeInfo.label})
                      </span>
                    </Badge>
                  );
                },
              );
            })()}
          </div>
        </div>
      </Renderify>
    </div>
  );
};

export default InteroperabilityConfig;
